import { Box, Typography } from "@mui/material";
import { OrionInput } from "@orion/OrionComponents/OrionInput";
import { useForm } from "@tanstack/react-form";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { z } from "zod";

import AuthLayout from "@/components/AuthLayout/AuthLayout";
import LoginFormSubmitButton from "@/components/LoginFormSubmitButton/LoginFormSubmitButton";
import { useAuth } from "@/contexts/AuthContext";
import {
  authLinkStyles,
  labelStyles,
  sxTextFieldStyles,
} from "@/routes/-authStyles";

export const Route = createFileRoute("/forgot-password")({
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: "/" });
    }
  },
  component: ForgotPasswordComponent,
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

function ForgotPasswordComponent() {
  const auth = useAuth();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    defaultValues: {
      email: "",
    },
    validators: {
      onSubmit: forgotPasswordSchema,
    },
    onSubmit: async ({ value }) => {
      await auth.sendPasswordReset(value.email);
      setSubmitted(true);
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void form.handleSubmit();
  };

  if (submitted) {
    return (
      <AuthLayout title="Check your email">
        <Typography
          sx={{
            color: "#fff",
            fontSize: "18px",
            fontWeight: 300,
            textAlign: "center",
          }}
        >
          If an account exists, a reset email has been sent.
        </Typography>
        <Box component={Link} to="/login" sx={authLinkStyles}>
          Back to login
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" onSubmit={handleSubmit}>
      <form.Field name="email">
        {(field) => (
          <OrionInput
            field={field}
            label="Email address"
            type="email"
            placeholder="Enter your email"
            sx={sxTextFieldStyles}
            labelStyles={labelStyles}
          />
        )}
      </form.Field>

      <LoginFormSubmitButton
        type="submit"
        label="Send reset link"
        disabled={form.state.isSubmitting}
      />

      <Box component={Link} to="/login" sx={authLinkStyles}>
        Back to login
      </Box>
    </AuthLayout>
  );
}
